import type { AuthState, RiskEntry, StatusRow, Task, UserRecord, WbsNode } from "./types";
import { DEFAULT_PHASES, type PhaseDef } from "./phases";

const PB_URL: string = import.meta.env.VITE_PB_URL ?? "";
export const AUTH_REQUIRED = !!PB_URL;

const AUTH_STORAGE_KEY = "crane_pb_auth";
const APP_STATE_COLLECTION = "app_state";

export function loadAuthFromStorage(): AuthState | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthState) : null;
  } catch {
    return null;
  }
}
export function saveAuthToStorage(a: AuthState) {
  try { localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(a)); } catch { /* ignore */ }
}
export function clearAuthStorage() {
  try { localStorage.removeItem(AUTH_STORAGE_KEY); } catch { /* ignore */ }
}

function authHeaders(auth: AuthState | null): Record<string, string> {
  return auth?.token ? { Authorization: auth.token } : {};
}

export class SessionExpiredError extends Error {
  constructor() { super("Session expired"); }
}

async function pbFetch(path: string, auth: AuthState | null, opts: RequestInit = {}): Promise<Response> {
  const res = await fetch(PB_URL + path, {
    ...opts,
    headers: { ...(opts.headers as Record<string, string> | undefined), ...authHeaders(auth) },
  });
  if (res.status === 401 && AUTH_REQUIRED) throw new SessionExpiredError();
  return res;
}

/**
 * A PocketBase call that has to succeed, with its response body parsed.
 *
 * PocketBase reports what was actually wrong ("Failed to create record", plus the
 * offending field) in the body, so that message is preferred over the bare status code
 * whenever there is one — a 400 on its own tells whoever's looking at the status line
 * nothing they can act on.
 */
async function pbJson(
  path: string,
  auth: AuthState | null,
  opts: RequestInit = {},
  fallbackMessage?: string
): Promise<Record<string, unknown>> {
  const res = await pbFetch(path, auth, opts);
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    throw new Error((body.message as string) || fallbackMessage || `HTTP ${res.status}`);
  }
  return (await res.json().catch(() => ({}))) as Record<string, unknown>;
}

/** The same, for a request carrying a JSON body. */
function pbSend(
  path: string,
  auth: AuthState | null,
  method: "POST" | "PATCH",
  payload: unknown,
  fallbackMessage?: string
): Promise<Record<string, unknown>> {
  return pbJson(path, auth, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, fallbackMessage);
}

export async function login(email: string, password: string): Promise<AuthState> {
  const res = await fetch(PB_URL + "/api/collections/users/auth-with-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identity: email, password }),
  });
  if (!res.ok) throw new Error(res.status === 400 ? "Wrong email or password." : `HTTP ${res.status}`);
  const body = await res.json();
  return { token: body.token, record: body.record as UserRecord };
}

export async function refreshAuth(): Promise<AuthState | null> {
  const stored = loadAuthFromStorage();
  if (!stored?.token) return null;
  try {
    const res = await fetch(PB_URL + "/api/collections/users/auth-refresh", {
      method: "POST",
      headers: { Authorization: stored.token },
    });
    if (!res.ok) throw new Error("expired");
    const body = await res.json();
    const auth: AuthState = { token: body.token, record: body.record as UserRecord };
    saveAuthToStorage(auth);
    return auth;
  } catch {
    clearAuthStorage();
    return null;
  }
}

export interface PlanningRecord {
  id: string;
  name: string;
  project: string;
  active?: boolean;
}

export async function listPlannings(auth: AuthState | null): Promise<PlanningRecord[]> {
  const body = await pbJson(`/api/collections/plannings/records?perPage=200&sort=name`, auth);
  return (body.items ?? []) as PlanningRecord[];
}

export async function createPlanningRecord(auth: AuthState | null, name: string, projectId: string): Promise<PlanningRecord> {
  const body = await pbSend(`/api/collections/plannings/records`, auth, "POST", { name, project: projectId, active: true });
  return body as unknown as PlanningRecord;
}

export interface ProjectRecord {
  id: string;
  name: string;
  /** Missing on records saved before this field existed — callers treat that as `true`. */
  useSwimLanes?: boolean;
  /** Whether this project works two shifts a day (07:00-15:00 and 15:00-23:00). Missing reads as `false`. */
  twoShifts?: boolean;
  /** This project's own swimlanes, in display order. Missing/empty falls back to `DEFAULT_PHASES`. */
  phases?: PhaseDef[];
  active?: boolean;
}

export async function listProjects(auth: AuthState | null): Promise<ProjectRecord[]> {
  const body = await pbJson(`/api/collections/projects/records?perPage=200&sort=name`, auth);
  return (body.items ?? []) as ProjectRecord[];
}

export async function createProjectRecord(auth: AuthState | null, name: string): Promise<ProjectRecord> {
  const body = await pbSend(`/api/collections/projects/records`, auth, "POST", { name, useSwimLanes: true, twoShifts: false, phases: DEFAULT_PHASES, active: true });
  return body as unknown as ProjectRecord;
}

export async function updateProjectRecord(auth: AuthState | null, id: string, patch: Partial<Pick<ProjectRecord, "useSwimLanes" | "twoShifts" | "phases">>): Promise<ProjectRecord> {
  const body = await pbSend(`/api/collections/projects/records/${id}`, auth, "PATCH", patch);
  return body as unknown as ProjectRecord;
}

export interface AppStateRecord {
  id: string;
  wbsNodes?: WbsNode[];
  statusRows?: StatusRow[];
  allowWeekendWork?: boolean;
  projectStartDate?: string | null;
  /** Civil date every task's `baseline` was last snapshotted from its `m`, or null if never set. */
  baselineSetAt?: string | null;
  subcontractorColors?: Record<string, string>;
  /** Undefined only for records saved before these lists existed — callers backfill from usage in that case. */
  knownSubcontractors?: string[];
  knownEquipment?: string[];
  knownPersonnel?: string[];
}

/** Project-wide scheduling settings — grouped so adding one doesn't grow the save signature again. */
export interface AppSettings {
  wbsNodes?: WbsNode[];
  allowWeekendWork: boolean;
  projectStartDate: string | null;
  /** Civil date every task's `baseline` was last snapshotted from its `m`, or null if never set. */
  baselineSetAt: string | null;
  /** Shared across everyone on the project, unlike theme — a crew agrees "blue = ABB" once. */
  subcontractorColors: Record<string, string>;
  /** The pick-lists behind the edit modal's dropdowns — explicit so an entry can be deleted for good, not just unused. */
  knownSubcontractors: string[];
  knownEquipment: string[];
  knownPersonnel: string[];
}

/** PocketBase filter expression scoping a query to one planning. IDs are the fixed
 *  `[a-z0-9]{15}` autogenerated shape, so no escaping beyond the quotes is needed. */
function planningFilter(planningId: string): string {
  return `planning="${planningId}"`;
}

export async function loadAppState(auth: AuthState | null, planningId: string): Promise<AppStateRecord | null> {
  if (!PB_URL) return null;
  const filter = encodeURIComponent(planningFilter(planningId));
  const body = await pbJson(`/api/collections/${APP_STATE_COLLECTION}/records?perPage=1&filter=${filter}`, auth);
  const items = body.items as AppStateRecord[] | undefined;
  return items?.length ? items[0] : null;
}

export async function saveAppState(auth: AuthState | null, recordId: string | null, planningId: string, statusRows: StatusRow[], settings: AppSettings): Promise<string> {
  if (!PB_URL) throw new Error("No persistence configured");
  const path = `/api/collections/${APP_STATE_COLLECTION}/records${recordId ? "/" + recordId : ""}`;
  const body = await pbSend(path, auth, recordId ? "PATCH" : "POST", { planning: planningId, statusRows, ...settings });
  // A JSON field the collection doesn't have is accepted and silently dropped, so the
  // only way to notice a missing one is that it doesn't come back in the saved record.
  if (settings.wbsNodes && !Array.isArray(body.wbsNodes)) {
    throw new Error("WBS storage is unavailable. Add the JSON field wbsNodes to the PocketBase app_state collection.");
  }
  if (statusRows.length && !Array.isArray(body.statusRows)) {
    throw new Error("Status row storage is unavailable. Add the JSON field statusRows to the PocketBase app_state collection.");
  }
  return body.id as string;
}

/** Pages through every record in a collection matching one planning — the default
 *  `perPage` (30) would silently truncate a real project's task/risk list. */
async function fetchAllRecords(collection: string, auth: AuthState | null, planningId: string): Promise<Record<string, unknown>[]> {
  const items: Record<string, unknown>[] = [];
  const filter = encodeURIComponent(planningFilter(planningId));
  let page = 1;
  for (;;) {
    const body = await pbJson(`/api/collections/${collection}/records?perPage=200&page=${page}&filter=${filter}`, auth);
    items.push(...((body.items as Record<string, unknown>[]) ?? []));
    if (page >= ((body.totalPages as number) ?? 1)) break;
    page++;
  }
  return items;
}

/**
 * One app-level item (a task, a risk) as stored on its own record, paired with the
 * PocketBase record id needed to PATCH/DELETE it later — the app's own stable id is
 * *not* that record id, which is why both have to be carried around together.
 */
export interface RecordEntry<T> { pbId: string; value: T }

/** How one kind of app item maps onto a PocketBase collection. */
interface RecordCodec<T> {
  collection: string;
  fromRecord(r: Record<string, unknown>): T;
  /** `planningId` is only passed on create — an existing record's planning is immutable. */
  toPayload(value: T, planningId?: string): Record<string, unknown>;
}

export interface RecordApi<T> {
  list(auth: AuthState | null, planningId: string): Promise<RecordEntry<T>[]>;
  create(auth: AuthState | null, planningId: string, value: T): Promise<string>;
  update(auth: AuthState | null, pbId: string, value: T): Promise<void>;
  remove(auth: AuthState | null, pbId: string): Promise<void>;
}

/**
 * The per-item CRUD every collection needs, given only how its records are shaped.
 * Tasks and risks each live in their own collection — one record per item, so two
 * people editing different tasks hit different records instead of clobbering one
 * shared blob — and the calls to manage them are identical apart from that shape.
 */
function recordApi<T>(codec: RecordCodec<T>): RecordApi<T> {
  const path = `/api/collections/${codec.collection}/records`;
  return {
    async list(auth, planningId) {
      const items = await fetchAllRecords(codec.collection, auth, planningId);
      return items.map((r) => ({ pbId: r.id as string, value: codec.fromRecord(r) }));
    },
    async create(auth, planningId, value) {
      const body = await pbSend(path, auth, "POST", codec.toPayload(value, planningId));
      return body.id as string;
    },
    async update(auth, pbId, value) {
      await pbSend(`${path}/${pbId}`, auth, "PATCH", codec.toPayload(value));
    },
    async remove(auth, pbId) {
      const res = await pbFetch(`${path}/${pbId}`, auth, { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    },
  };
}

function taskFromRecord(r: Record<string, unknown>): Task {
  return {
    id: r.taskId as string,
    wbsId: (r.wbsId as string) || null,
    name: r.name as string,
    phase: r.phase as Task["phase"],
    planDay: r.planDay as string,
    baseline: r.baseline as number,
    o: r.o as number,
    m: r.m as number,
    p: r.p as number,
    risk: r.risk as string,
    type: r.type as Task["type"],
    preds: (r.preds as string[]) ?? [],
    pinnedDay: (r.pinnedDay as number) ?? null,
    pinnedShift: (r.pinnedShift as 1 | 2) ?? null,
    weekendWork: (r.weekendWork as Task["weekendWork"]) ?? "auto",
    doubleShift: !!r.doubleShift,
    subcontractors: (r.subcontractors as Task["subcontractors"]) ?? [],
    responsibleParty: (r.responsibleParty as Task["responsibleParty"]) ?? "",
    requiredEquipment: (r.requiredEquipment as string) ?? "",
    requiredPersonnel: (r.requiredPersonnel as string) ?? "",
    doneDays: (r.doneDays as number[]) ?? [],
    blocked: !!r.blocked,
    blockedReason: (r.blockedReason as string) ?? "",
    blockedSince: (r.blockedSince as number) ?? null,
    requiredStatus: (r.requiredStatus as Task["requiredStatus"]) ?? [],
    actualStart: (r.actualStart as number) ?? null,
    actualFinish: (r.actualFinish as number) ?? null,
  };
}

function taskToPayload(t: Task, planningId?: string): Record<string, unknown> {
  return {
    ...(planningId ? { planning: planningId } : {}),
    taskId: t.id, wbsId: t.wbsId ?? null, name: t.name, phase: t.phase, planDay: t.planDay,
    baseline: t.baseline, o: t.o, m: t.m, p: t.p, risk: t.risk, type: t.type, preds: t.preds,
    pinnedDay: t.pinnedDay, pinnedShift: t.pinnedShift ?? null, weekendWork: t.weekendWork ?? "auto", doubleShift: !!t.doubleShift,
    subcontractors: t.subcontractors, responsibleParty: t.responsibleParty,
    requiredEquipment: t.requiredEquipment, requiredPersonnel: t.requiredPersonnel, doneDays: t.doneDays,
    blocked: t.blocked, blockedReason: t.blockedReason, blockedSince: t.blockedSince,
    requiredStatus: t.requiredStatus,
    actualStart: t.actualStart ?? null, actualFinish: t.actualFinish ?? null,
  };
}

export const taskRecords: RecordApi<Task> = recordApi({
  collection: "tasks",
  fromRecord: taskFromRecord,
  toPayload: taskToPayload,
});

function riskFromRecord(r: Record<string, unknown>): RiskEntry {
  return {
    id: r.riskId as string,
    desc: (r.desc as string) ?? "",
    probability: (r.probability as RiskEntry["probability"]) ?? "",
    impact: (r.impact as RiskEntry["impact"]) ?? "",
    owner: (r.owner as string) ?? "",
    mitigation: (r.mitigation as string) ?? "",
    status: (r.status as RiskEntry["status"]) ?? "Needs input",
  };
}

function riskToPayload(r: RiskEntry, planningId?: string): Record<string, unknown> {
  return {
    ...(planningId ? { planning: planningId } : {}),
    riskId: r.id, desc: r.desc, probability: r.probability, impact: r.impact, owner: r.owner, mitigation: r.mitigation, status: r.status,
  };
}

export const riskRecords: RecordApi<RiskEntry> = recordApi({
  collection: "risks",
  fromRecord: riskFromRecord,
  toPayload: riskToPayload,
});

export async function listUsers(auth: AuthState | null): Promise<UserRecord[]> {
  const body = await pbJson("/api/collections/users/records?perPage=200&sort=email", auth);
  return (body.items ?? []) as UserRecord[];
}

export async function updateUserRole(auth: AuthState | null, userId: string, role: string): Promise<void> {
  await pbSend(`/api/collections/users/records/${userId}`, auth, "PATCH", { role });
}

export async function deleteUser(auth: AuthState | null, userId: string): Promise<void> {
  const res = await pbFetch(`/api/collections/users/records/${userId}`, auth, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export function avatarUrl(record: UserRecord): string | null {
  if (!record.avatar) return null;
  return `${PB_URL}/api/files/users/${record.id}/${record.avatar}`;
}

export async function updateProfile(
  auth: AuthState | null,
  patch: { name?: string; avatarFile?: File | null; removeAvatar?: boolean }
): Promise<UserRecord> {
  if (!auth) throw new Error("Not signed in.");
  const form = new FormData();
  if (patch.name !== undefined) form.append("name", patch.name);
  if (patch.avatarFile) form.append("avatar", patch.avatarFile);
  else if (patch.removeAvatar) form.append("avatar", "");
  const res = await pbFetch(`/api/collections/users/records/${auth.record.id}`, auth, {
    method: "PATCH",
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `HTTP ${res.status}`);
  }
  return (await res.json()) as UserRecord;
}

export async function changeOwnPassword(auth: AuthState | null, oldPassword: string, password: string): Promise<void> {
  if (!auth) throw new Error("Not signed in.");
  await pbSend(`/api/collections/users/records/${auth.record.id}`, auth, "PATCH",
    { oldPassword, password, passwordConfirm: password }, "Couldn't change password.");
}

export async function inviteUser(auth: AuthState | null, email: string, password: string, role: string): Promise<void> {
  await pbSend("/api/collections/users/records", auth, "POST",
    { email, password, passwordConfirm: password, role, emailVisibility: true }, "Couldn't add user.");
}
